import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoPlayer from "@/components/base/VideoPlayer";
import api from "@/services/api";
import { useTranslation } from "@/context/TranslationContext";

interface PortfolioItem {
  id: string;
  title: string;
  category: string;
  description: string;
  thumbnail_url: string | null;
  video_url: string | null;
  video_type: string;
  tech_stack: string[];
  featured: boolean;
  year: string | null;
  client_name: string | null;
  duration: string | null;
  team_size: string | null;
  overview: string | null;
  challenge: string | null;
  solution: string | null;
  results: { metric: string; value: string; description: string }[];
  gallery: string[];
  tech_breakdown: { layer: string; tools: string[] }[];
  live_url: string | null;
  user_manual_url?: string | null;
  collaborators?: string[];
}

export default function PortfolioDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, lang, translateText } = useTranslation();
  const [project, setProject] = useState<PortfolioItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [translatedContent, setTranslatedContent] = useState<{ overview?: string; challenge?: string; solution?: string }>({});


  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get(`/portfolio/${id}`)
      .then((res) => setProject(res.data.data))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Explicitly translate the three key sections when project loads and language is French
  useEffect(() => {
    if (!loading && project && lang === "fr") {
      const translateSections = async () => {
        const translations: { overview?: string; challenge?: string; solution?: string } = {};
        
        if (project.overview) {
          translations.overview = await translateText(project.overview);
        }
        if (project.challenge) {
          translations.challenge = await translateText(project.challenge);
        }
        if (project.solution) {
          translations.solution = await translateText(project.solution);
        }
        
        setTranslatedContent(translations);
      };
      
      translateSections();
    } else if (lang === "en") {
      // Clear translations when switching back to English
      setTranslatedContent({});
    }
  }, [loading, project, lang, translateText]);

  const tech = Array.isArray(project?.tech_stack) ? project.tech_stack : [];
  const galleryComputed = useMemo(() => project ? (Array.isArray(project.gallery) ? project.gallery : []) : [], [project]);
  const results = Array.isArray(project?.results) ? project.results : [];
  const techBreakdown = Array.isArray(project?.tech_breakdown) ? project.tech_breakdown : [];
  const collaborators = Array.isArray(project?.collaborators) ? project.collaborators : [];
  const liveUrl = project?.live_url || "";
  const userManualUrl = project?.user_manual_url || "";

  const convertDriveLink = (url: string) => {
    console.log('Converting URL:', url);
    if (url.includes('drive.google.com')) {
      // Try different Google Drive URL formats
      let fileId = null;

      // Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
      const fileMatch = url.match(/\/file\/d\/([^\/]+)/);
      if (fileMatch) {
        fileId = fileMatch[1];
        console.log('Matched file format, fileId:', fileId);
      } else {
        // Format: https://drive.google.com/open?id=FILE_ID or https://drive.google.com/uc?id=FILE_ID
        const idMatch = url.match(/[?&]id=([^&]+)/);
        if (idMatch) {
          fileId = idMatch[1];
          console.log('Matched id format, fileId:', fileId);
        }
      }

      if (fileId) {
        const converted = `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
        console.log('Converted to:', converted);
        return converted;
      } else {
        console.log('No fileId found');
      }
    } else {
      console.log('Not a Google Drive URL');
    }
    return url;
  };

  const thumbnail = convertDriveLink(project?.thumbnail_url || "");
  const videoUrl = project?.video_url || "";
  const videoType = (project?.video_type === "direct" ? "upload" : project?.video_type) as "youtube" | "vimeo" | "embed" | "upload";
  const client = project?.client_name || "";
  const duration = project?.duration || "";
  const teamSize = project?.team_size || "";
  const year = project?.year || "";
  const gallery = galleryComputed;



  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black flex flex-col items-center justify-center gap-4">
        <Navbar />
        <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-deep-black flex flex-col items-center justify-center gap-4">
        <Navbar />
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-dark-card border border-dark-border mx-auto mb-6">
            <i className="ri-file-unknow-line text-3xl text-silver-dim" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Project Not Found
          </h1>
          <p className="text-silver-dim text-sm mb-6">This project doesn&apos;t exist or has been removed.</p>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black font-inter">
      <Navbar />

      {/* Breadcrumb */}
      <div className="relative pt-20 px-4 md:px-6 border-b border-dark-border bg-dark-panel/50 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-r from-electric-blue/5 via-transparent to-transparent opacity-40" />
          <div className="absolute inset-0 bg-circuit-pattern opacity-10" />
        </div>
        <div className="max-w-7xl mx-auto py-4 flex items-center gap-2 text-sm relative z-10">
          <Link to="/" className="text-silver-dim hover:text-electric-blue transition-colors cursor-pointer whitespace-nowrap">
            Home
          </Link>
          <i className="ri-arrow-right-s-line text-silver-dim" />
          <Link to="/portfolio" className="text-silver-dim hover:text-electric-blue transition-colors cursor-pointer whitespace-nowrap">
            Portfolio
          </Link>
          <i className="ri-arrow-right-s-line text-silver-dim" />
          <span className="text-silver truncate">{project.title}</span>
        </div>
      </div>

      {/* Hero — Video First */}
      <section className="relative px-4 md:px-6 py-12 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-electric-blue/20 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-electric-blue/5 via-transparent to-transparent opacity-40" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            {/* Left: Video + Gallery */}
            <div className="lg:col-span-3 space-y-4">
              {/* Main Video Player */}
              {videoUrl ? (
                <VideoPlayer
                  url={videoUrl}
                  type={videoType}
                  thumbnail={thumbnail}
                  title={`${project.title} — Demo`}
                  className="aspect-video"
                  autoplay
                />
              ) : (
                <div className="aspect-video bg-dark-card rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-film-line text-6xl text-silver-dim mb-4 block" />
                    <p className="text-silver text-sm">No demo video available</p>
                  </div>
                </div>
              )}

              {/* Gallery Thumbnails */}
              {gallery.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => { setActiveGalleryIndex(i); setLightboxOpen(true); }}
                      className={`relative rounded-xl overflow-hidden aspect-video cursor-pointer group border-2 transition-all duration-200 ${
                        activeGalleryIndex === i ? "border-electric-blue" : "border-transparent hover:border-electric-blue/40"
                      }`}
                    >
                        <img
                          src={convertDriveLink(img)}
                          alt={`${project.title} screenshot ${i + 1}`}
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            console.log('Image failed to load:', e.currentTarget.src);
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                              const placeholder = document.createElement('div');
                              placeholder.className = 'absolute inset-0 flex items-center justify-center bg-dark-card';
                              placeholder.innerHTML = '<div class="text-center text-silver-dim"><i class="ri-image-line text-2xl mb-1 block"></i><span class="text-xs">Image not available</span></div>';
                              parent.appendChild(placeholder);
                            }
                          }}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        />
                      <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-black/60">
                          <i className="ri-zoom-in-line text-white text-sm" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Project Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Category + Year */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-electric-blue/10 border border-electric-blue/30 text-electric-blue font-semibold uppercase tracking-wider">
                  {project.category}
                </span>
                <span className="text-xs px-3 py-1 rounded-full bg-dark-card border border-dark-border text-silver-dim">
                  {year}
                </span>
                {project.featured && (
                  <span className="text-xs px-3 py-1 rounded-full bg-cyber-orange/10 border border-cyber-orange/30 text-cyber-orange font-semibold">
                    Featured
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-black text-white leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {project.title}
              </h1>

              <p className="text-silver text-sm leading-relaxed">{project.description}</p>

              {/* Meta Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-building-line", label: "Client", value: client },
                  { icon: "ri-time-line", label: "Duration", value: duration },
                  { icon: "ri-team-line", label: "Team Size", value: teamSize },
                  { icon: "ri-calendar-line", label: "Year", value: year },
                ].map((meta) => (
                  <div key={meta.label} className="bg-dark-card border border-dark-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${meta.icon} text-electric-blue text-sm`} />
                      </div>
                      <span className="text-silver-dim text-xs uppercase tracking-wider">{meta.label}</span>
                    </div>
                    <span className="text-white text-sm font-semibold">{meta.value}</span>
                  </div>
                ))}
              </div>

              {/* Tech Stack */}
              <div>
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-3">Tech Stack</h3>
                <div className="flex flex-wrap gap-2">
                  {tech.map((t) => (
                    <span
                      key={t}
                      className="text-xs px-3 py-1.5 rounded-lg bg-dark-card border border-dark-border text-silver hover:border-electric-blue/40 hover:text-electric-blue transition-colors"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Collaborators */}
              {collaborators.length > 0 && (
                <div>
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-3">Collaborators</h3>
                  <div className="flex flex-wrap gap-2">
                    {collaborators.map((c) => (
                      <span
                        key={c}
                        className="text-xs px-3 py-1.5 rounded-lg bg-dark-card border border-dark-border text-silver hover:border-electric-blue/40 hover:text-electric-blue transition-colors"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-col gap-3 pt-2">
                {liveUrl && (
                  <a
                    href={liveUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-electric-blue text-deep-black font-bold py-3 rounded-xl hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-external-link-line" />
View Live Project
                  </a>
                )}
                {userManualUrl && (
                  <a
                    href={userManualUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-dark-card border border-dark-border text-silver hover:border-electric-blue/40 hover:text-white py-3 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-file-pdf-line" />
View User Manual
                  </a>
                )}
                <a
                  href="/#contact"
                  className="flex items-center justify-center gap-2 border border-dark-border text-silver hover:border-electric-blue/40 hover:text-white py-3 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-message-line" />
                  Build Something Similar
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results / Metrics */}
      {results.length > 0 && (
        <section className="px-4 md:px-6 py-14 bg-dark-panel border-t border-dark-border">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 bg-electric-blue rounded-full" />
              <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
Results & Impact
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {results.map((result) => (
                <div
                  key={result.metric}
                  className="bg-dark-card border border-dark-border rounded-2xl p-6 hover:border-electric-blue/30 transition-all duration-300"
                >
                  <div className="text-3xl md:text-4xl font-black text-electric-blue mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {result.value}
                  </div>
                  <div className="text-white font-semibold text-sm mb-1">{result.metric}</div>
                  <div className="text-silver-dim text-xs leading-relaxed">{result.description}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Overview / Challenge / Solution */}
      <section className="px-4 md:px-6 py-14">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {[
              { icon: "ri-eye-line", label: "Overview", color: "text-electric-blue", content: translatedContent.overview || project.overview },
              { icon: "ri-error-warning-line", label: "The Challenge", color: "text-cyber-red", content: translatedContent.challenge || project.challenge },
              { icon: "ri-lightbulb-line", label: "Our Solution", color: "text-cyber-green", content: translatedContent.solution || project.solution },
            ].map((section) => (
              <div key={section.label} className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-dark-card border border-dark-border ${section.color}`}>
                    <i className={`${section.icon} text-lg`} />
                  </div>
                  <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {section.label}
                  </h3>
                </div>
                <p className="text-silver text-sm leading-relaxed">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Breakdown */}
      {techBreakdown.length > 0 && (
        <section className="px-4 md:px-6 py-14 bg-dark-panel border-t border-dark-border">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-1 h-8 bg-electric-blue rounded-full" />
              <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
Technology Breakdown
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {techBreakdown.map((layer, i) => {
                const layerColors = [
                  "border-electric-blue/30 bg-electric-blue/5",
                  "border-cyber-green/30 bg-cyber-green/5",
                  "border-cyber-orange/30 bg-cyber-orange/5",
                  "border-cyber-red/30 bg-cyber-red/5",
                  "border-silver/20 bg-silver/5",
                ];
                const textColors = [
                  "text-electric-blue",
                  "text-cyber-green",
                  "text-cyber-orange",
                  "text-cyber-red",
                  "text-silver",
                ];
                return (
                  <div
                    key={layer.layer}
                    className={`rounded-2xl border p-5 ${layerColors[i % layerColors.length]}`}
                  >
                    <div className={`text-xs font-bold uppercase tracking-widest mb-4 ${textColors[i % textColors.length]}`}>
                      {layer.layer}
                    </div>
                    <ul className="space-y-2">
                      {layer.tools.map((tool) => (
                        <li key={tool} className="flex items-center gap-2 text-sm text-silver">
                          <div className="w-3 h-3 flex items-center justify-center flex-shrink-0">
                            <i className={`ri-checkbox-blank-circle-fill text-xs ${textColors[i % textColors.length]} opacity-60`} />
                          </div>
                          {tool}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Lightbox */}
      {lightboxOpen && gallery.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full bg-dark-card border border-dark-border text-silver hover:text-white cursor-pointer z-10"
            >
              <i className="ri-close-line text-lg" />
            </button>

            {/* Image */}
            <img
              src={convertDriveLink(gallery[activeGalleryIndex])}
              alt={`${project.title} screenshot ${activeGalleryIndex + 1}`}
              referrerPolicy="no-referrer"
              onError={(e) => {
                console.log('Lightbox image failed to load:', e.currentTarget.src);
                e.currentTarget.style.display = 'none';
                const container = e.currentTarget.parentElement;
                if (container) {
                  const placeholder = document.createElement('div');
                  placeholder.className = 'flex items-center justify-center h-96 bg-dark-card rounded-2xl';
                  placeholder.innerHTML = '<div class="text-center text-silver-dim"><i class="ri-image-line text-4xl mb-2 block"></i><span>Image not available</span></div>';
                  container.appendChild(placeholder);
                }
              }}
              className="w-full rounded-2xl object-cover max-h-[70vh]"
            />

            {/* Navigation */}
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => setActiveGalleryIndex((prev) => (prev - 1 + gallery.length) % gallery.length)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 cursor-pointer transition-all"
              >
                <i className="ri-arrow-left-line" />
              </button>
              <span className="text-silver-dim text-sm">
                {activeGalleryIndex + 1} / {gallery.length}
              </span>
              <button
                onClick={() => setActiveGalleryIndex((prev) => (prev + 1) % gallery.length)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 cursor-pointer transition-all"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 mt-4 justify-center overflow-x-auto">
              {gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveGalleryIndex(i)}
                  className={`flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${
                    i === activeGalleryIndex ? "border-electric-blue" : "border-transparent hover:border-electric-blue/40"
                  }`}
                >
                  <img
                    src={convertDriveLink(img)}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      console.log('Thumbnail failed to load:', e.currentTarget.src);
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div class="flex items-center justify-center w-full h-full bg-dark-card"><i class="ri-image-line text-silver-dim text-xs"></i></div>`;
                      }
                    }}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      <section className="px-4 md:px-6 py-16 bg-dark-panel border-t border-dark-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-black text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
Inspired by this project?
          </h2>
          <p className="text-silver text-sm mb-8">
Let&apos;s build something just as powerful for your business. Tell us about your vision.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/#contact"
              className="flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-8 py-3.5 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
Start a Project
              <i className="ri-arrow-right-line" />
            </a>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 border border-dark-border text-silver hover:border-electric-blue/40 hover:text-white px-8 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
Go Back
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

